import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';
import { ViewIcon, EditIcon, DeleteIcon } from '../components/IconButtons';
import AdminSidebar from '../components/AdminSidebar';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';

const AdminStokObatPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    purpose: '',
    dosage: '',
    expired: '',
    instruction: '',
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [medicineToDelete, setMedicineToDelete] = useState(null);
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: true // Open by default since we're in a master data page
  });
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchMedicines = async () => {
      setLoading(true);
      try {
        const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/medicines?page=${currentPage}`;
        if (searchQuery && searchQuery.trim() !== '') {
          apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
        }
        const response = await authFetch(apiUrl);
        if (!response.ok) throw new Error('Failed to fetch medicines');
        const result = await response.json();
        setData(result.data);
        setLastPage(result.last_page);
        setTotal(result.total);
      } catch (err) {
        setNotification({ show: true, message: 'Gagal memuat data obat: ' + err.message, type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchMedicines();
  }, [currentPage, searchQuery]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setFormData({ name: '', purpose: '', dosage: '', expired: '', instruction: '' });
    setShowAddModal(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL.replace(/\/$/, '')}/api/medicines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Gagal menambah obat');
      }
      const newMedicine = await response.json();
      setData((prev) => [...prev, newMedicine]);
      setShowAddModal(false);
      setNotification({ show: true, message: 'Obat berhasil ditambahkan!', type: 'success' });
    } catch (err) {
      setNotification({ show: true, message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleView = (medicine) => {
    setSelectedMedicine(medicine);
    setShowViewModal(true);
  };

  const handleEdit = (medicine) => {
    setSelectedMedicine(medicine);
    setFormData({ ...medicine });
    setShowEditModal(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL.replace(/\/$/, '')}/api/medicines/${selectedMedicine.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Gagal mengedit obat');
      }
      const updated = await response.json();
      setData((prev) => prev.map((item) => item.id === selectedMedicine.id ? updated : item));
      setNotification({ show: true, message: 'Obat berhasil diupdate!', type: 'success' });
      setShowEditModal(false);
    } catch (err) {
      setNotification({ show: true, message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (row) => {
    setMedicineToDelete(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!medicineToDelete) return;
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL.replace(/\/$/, '')}/api/medicines/${medicineToDelete.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Gagal menghapus obat');
      setData((prev) => prev.filter((item) => item.id !== medicineToDelete.id));
      setNotification({ show: true, message: 'Obat berhasil dihapus!', type: 'success' });
    } catch (err) {
      setNotification({ show: true, message: err.message, type: 'error' });
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setMedicineToDelete(null);
    }
  };

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <h1>Stok Obat</h1>
          <p>Kelola stok obat dengan mudah dan efisien</p>
        </div>
      </PageHeader>
      
      <DashboardGrid>
        <AdminSidebar 
          user={adminData} 
          accordionState={accordionState} 
          setAccordionState={setAccordionState} 
        />
        <MainSection>
          <ActionHeader>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Cari obat"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
            <AddButton onClick={handleAdd}>+ Tambah Obat</AddButton>
          </ActionHeader>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Nama Obat</th>
                <th>Tujuan</th>
                <th>Dosis</th>
                <th>Instruksi</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}>Memuat data obat...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={6}>Tidak ada data obat.</td></tr>
              ) : (
                data.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.purpose}</td>
                    <td>{row.dosage}</td>
                    <td>{row.instruction}</td>
                    <td>
                      <Button onClick={() => handleView(row)} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Lihat">
                        <ViewIcon />
                      </Button>
                      <Button onClick={() => handleEdit(row)} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Edit">
                        <EditIcon />
                      </Button>
                      <DeleteButton onClick={() => handleDelete(row)} style={{ padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Hapus">
                        <DeleteIcon />
                      </DeleteButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <PaginationContainer>
            <PaginationInfo>
              Showing {data.length} of {total} entries
            </PaginationInfo>
            <PaginationButtons>
              <PaginationButton 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                disabled={currentPage === 1}
              >
                Previous
              </PaginationButton>
              <PageInfo>Page {currentPage} of {lastPage}</PageInfo>
              <PaginationButton 
                onClick={() => setCurrentPage(prev => prev < lastPage ? prev + 1 : prev)}
                disabled={currentPage === lastPage}
              >
                Next
              </PaginationButton>
            </PaginationButtons>
          </PaginationContainer>
        </TableContainer>

        {/* Notification Toast */}
        {showViewModal && selectedMedicine && (
          <Modal>
            <ModalContent>
              <ModalHeader>
                <h2>Detail Obat</h2>
                <CloseButton onClick={() => setShowViewModal(false)}>&times;</CloseButton>
              </ModalHeader>
              <div>
                <FormGroup>
                  <Label>Nama Obat</Label>
                  <div>{selectedMedicine.name}</div>
                </FormGroup>
                <FormGroup>
                  <Label>Kegunaan</Label>
                  <div>{selectedMedicine.purpose}</div>
                </FormGroup>
                <FormGroup>
                  <Label>Dosis</Label>
                  <div>{selectedMedicine.dosage}</div>
                </FormGroup>
                <FormGroup>
                  <Label>Tanggal Kadaluarsa</Label>
                  <div>{selectedMedicine.expired}</div>
                </FormGroup>
                <FormGroup>
                  <Label>Instruksi Penggunaan</Label>
                  <div>{selectedMedicine.instruction}</div>
                </FormGroup>
                <ButtonGroup>
                  <CancelButton onClick={() => setShowViewModal(false)}>Tutup</CancelButton>
                </ButtonGroup>
              </div>
            </ModalContent>
          </Modal>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <Modal>
            <ModalContent>
              <ModalHeader>
                <h2>Konfirmasi Hapus</h2>
                <CloseButton onClick={() => setShowDeleteModal(false)}>&times;</CloseButton>
              </ModalHeader>
              <div>Apakah Anda yakin ingin menghapus obat ini?</div>
              <ButtonGroup style={{ marginTop: '2rem' }}>
                <CancelButton type="button" onClick={() => setShowDeleteModal(false)}>Batal</CancelButton>
                <DeleteButton type="button" onClick={confirmDelete}>Hapus</DeleteButton>
              </ButtonGroup>
            </ModalContent>
          </Modal>
        )}

        {/* Notification Toast */}
        {notification.show && (
          <NotificationToast $type={notification.type}>
            <NotificationMessage>{notification.message}</NotificationMessage>
            <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
              ×
            </CloseNotificationButton>
          </NotificationToast>
        )}

        {/* Add Medicine Modal */}
        {showAddModal && (
          <Modal>
            <ModalContent>
              <ModalHeader>
                <h2>Tambah Obat</h2>
                <CloseButton onClick={() => setShowAddModal(false)}>&times;</CloseButton>
              </ModalHeader>
              <form onSubmit={handleSubmitAdd}>
                <FormGroup>
                  <Label>Nama Obat</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Tujuan</Label>
                  <Input
                    type="text"
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Dosis</Label>
                  <Input
                    type="text"
                    name="dosage"
                    value={formData.dosage}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Kadaluarsa</Label>
                  <Input
                    type="date"
                    name="expired"
                    value={formData.expired}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Instruksi</Label>
                  <Input
                    type="text"
                    name="instruction"
                    value={formData.instruction}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <ButtonGroup>
                  <CancelButton type="button" onClick={() => setShowAddModal(false)}>
                    Batal
                  </CancelButton>
                  <SubmitButton type="submit">
                    Simpan
                  </SubmitButton>
                </ButtonGroup>
              </form>
            </ModalContent>
          </Modal>
        )}

        {/* Edit Medicine Modal */}
        {showEditModal && (
          <Modal>
            <ModalContent>
              <ModalHeader>
                <h2>Edit Obat</h2>
                <CloseButton onClick={() => setShowEditModal(false)}>&times;</CloseButton>
              </ModalHeader>
              <form onSubmit={handleSubmitEdit}>
                <FormGroup>
                  <Label>Nama Obat</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Tujuan</Label>
                  <Input
                    type="text"
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Dosis</Label>
                  <Input
                    type="text"
                    name="dosage"
                    value={formData.dosage}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Kadaluarsa</Label>
                  <Input
                    type="date"
                    name="expired"
                    value={formData.expired}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Instruksi</Label>
                  <Input
                    type="text"
                    name="instruction"
                    value={formData.instruction}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <ButtonGroup>
                  <CancelButton type="button" onClick={() => setShowEditModal(false)}>
                    Batal
                  </CancelButton>
                  <SubmitButton type="submit">
                    Simpan Perubahan
                  </SubmitButton>
                </ButtonGroup>
              </form>
            </ModalContent>
          </Modal>
        )}
        </MainSection>
      </DashboardGrid>
    </PageContainer>
  );
};

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;
  @media (max-width: 1100px) {
    order: 1;
  }
`;

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-light);
    margin: 0;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const TableContainer = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  th {
    background-color: var(--background-light);
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: var(--background-light);
  }
`;

const ActionButton = styled.button`
  background-color: ${props => props.$danger ? 'var(--error)' : 'var(--primary)'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const AddButton = styled.button`
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;

  &:hover {
    background-color: #219150;
    opacity: 0.95;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  border: 1px solid var(--border);
  color: var(--text);
`;

const DeleteButton = styled.button`
  background-color: #e74c3c !important;
  color: #fff !important;
  font-weight: bold;
  border: 2px solid #c0392b;
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
  letter-spacing: 0.5px;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s, background-color 0.2s;
  &:hover, &:focus {
    background-color: #c0392b !important;
    outline: 2px solid #e74c3c;
    color: #fff !important;
    opacity: 0.95;
  }
`;


// Notification Toast Styles
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  color: #6c757d;
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  background-color: white;
  color: var(--primary);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: #f5f6fa;
  }
`;

const PageInfo = styled.span`
  color: #6c757d;
`;

export default AdminStokObatPage;